/**
 * <application-matrix>
 *
 * Filters the product grid by surface and concern. Chips are hidden in the
 * markup and revealed here, so a page without JavaScript shows every product
 * as a plain grid rather than a set of controls that do nothing.
 *
 * Matching rules:
 *   - within a row (surfaces, concerns) selections are OR
 *   - across the two rows they are AND
 * So "Driveways + Steps" widens, and "Driveways + Pet safe" narrows — which is
 * how people expect filters to behave.
 */

class ApplicationMatrix extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-matrix-grid]');
    if (!this.grid) return;

    this.cards = Array.from(this.querySelectorAll('[data-matrix-card]'));
    this.chips = Array.from(this.querySelectorAll('[data-matrix-chip]'));
    this.filters = this.querySelector('[data-matrix-filters]');
    this.countEl = this.querySelector('[data-matrix-count]');
    this.emptyEl = this.querySelector('[data-matrix-empty]');
    this.resetButtons = Array.from(this.querySelectorAll('[data-matrix-reset]'));

    this.selected = { surfaces: new Set(), concerns: new Set() };

    // only reveal the controls once we can actually run them
    if (this.filters) this.filters.hidden = false;

    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => this.toggle(chip));
    });
    this.resetButtons.forEach((b) => {
      b.addEventListener('click', () => this.reset());
    });

    this.apply();
  }

  toggle(chip) {
    const group = chip.dataset.group;
    const value = chip.dataset.value;
    const set = this.selected[group];
    if (!set) return;

    if (set.has(value)) {
      set.delete(value);
      chip.setAttribute('aria-pressed', 'false');
    } else {
      set.add(value);
      chip.setAttribute('aria-pressed', 'true');
    }
    this.apply();
  }

  reset() {
    this.selected.surfaces.clear();
    this.selected.concerns.clear();
    this.chips.forEach((c) => c.setAttribute('aria-pressed', 'false'));
    this.apply();
  }

  matches(card) {
    const has = (attr, set) => {
      if (set.size === 0) return true;
      const values = (card.dataset[attr] || '').split(/\s+/).filter(Boolean);
      for (const v of set) {
        if (values.includes(v)) return true;
      }
      return false;
    };
    return has('surfaces', this.selected.surfaces) && has('concerns', this.selected.concerns);
  }

  apply() {
    let shown = 0;
    this.cards.forEach((card) => {
      const ok = this.matches(card);
      card.hidden = !ok;
      if (ok) shown += 1;
    });

    const active = this.selected.surfaces.size + this.selected.concerns.size;

    if (this.countEl) {
      if (active === 0) {
        this.countEl.textContent = '';
      } else {
        this.countEl.textContent =
          shown === 1 ? '1 product matches' : `${shown} products match`;
      }
    }

    if (this.emptyEl) this.emptyEl.hidden = shown !== 0;
    if (this.grid) this.grid.hidden = shown === 0;

    this.resetButtons.forEach((b) => {
      // the reset inside the empty state is always useful; the header one only
      // once something is actually filtered
      if (this.emptyEl && this.emptyEl.contains(b)) return;
      b.hidden = active === 0;
    });
  }
}

if (!customElements.get('application-matrix')) {
  customElements.define('application-matrix', ApplicationMatrix);
}
